import React, { useEffect, useState } from "react";
import { ClipboardList, Users, Check, ChefHat, X, Send, CheckCircle2, ShoppingBag, Truck, Utensils, Undo2, Phone, MapPin, Banknote } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const COURSES = [
  { n: 1, label: "Antipasti", emoji: "🥗", color: "#D99B26" },
  { n: 2, label: "Primi", emoji: "🍝", color: "#C85A32" },
  { n: 3, label: "Secondi", emoji: "🥩", color: "#3B503A" },
  { n: 4, label: "Dessert", emoji: "🍰", color: "#8C580C" },
  { n: 0, label: "Bevande", emoji: "🥤", color: "#6B625B" },
];
const COURSE_MAP = COURSES.reduce((acc, c) => (acc[c.n] = c, acc), {});

const ORDER_TYPE = {
  dine_in: { label: "Sala", icon: Utensils, color: "#3B503A" },
  takeaway: { label: "Asporto", icon: ShoppingBag, color: "#C85A32" },
  delivery: { label: "Delivery", icon: Truck, color: "#D99B26" },
};

function fmtTime(iso) { try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } }

const TABS = [
  { id: "new", label: "Nuovi", icon: ClipboardList },
  { id: "preparing", label: "In preparazione", icon: ChefHat },
  { id: "served", label: "Serviti", icon: Check },
];

function OrderCard({ o, onSendCourse, onUndoCourse, onClose, onMarkServed, onSetStatus }) {
  const [confirmUndo, setConfirmUndo] = useState(null);
  const itemsByCourse = {};
  (o.items || []).forEach((it) => {
    const c = (it.course ?? 3);
    if (!itemsByCourse[c]) itemsByCourse[c] = [];
    itemsByCourse[c].push(it);
  });
  const orderedCourses = Object.keys(itemsByCourse).map(Number).sort((a, b) => {
    if (a === 0) return -1; if (b === 0) return 1;
    return a - b;
  });
  const sent = new Set(o.sent_courses || []);
  const allSent = orderedCourses.every((c) => sent.has(c));
  const otype = ORDER_TYPE[o.order_type || "dine_in"] || ORDER_TYPE.dine_in;
  const TypeIcon = otype.icon;

  return (
    <div data-testid={`order-${o.id}`} className="rounded-2xl border border-[#E8E2D5] bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider text-white" style={{ background: otype.color }}>
              <TypeIcon size={10}/> {otype.label}
            </span>
            {o.payment_method === "cash" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-[#FEF3D6] text-[#8C580C]">
                <Banknote size={10}/> Contanti
              </span>
            )}
          </div>
          <div className="font-heading font-semibold text-sm truncate mt-1">
            {o.guest_name}
            {o.order_type === "dine_in" && o.table_number && <span className="text-[#C85A32]"> · T{o.table_number}</span>}
          </div>
          <div className="text-[11px] text-[#6B625B]">{fmtTime(o.created_at)} · € {o.total.toFixed(2)}</div>
          {o.order_type !== "dine_in" && (
            <div className="mt-1.5 space-y-0.5 text-[11px] text-[#6B625B]">
              {o.customer_phone && <div className="inline-flex items-center gap-1"><Phone size={10}/> <a href={`tel:${o.customer_phone}`} className="underline">{o.customer_phone}</a></div>}
              {o.delivery_address && <div className="flex items-start gap-1"><MapPin size={10} className="mt-0.5 flex-shrink-0"/> <span>{o.delivery_address}</span></div>}
              {o.pickup_time && <div className="text-[#C85A32] font-bold">⏱ {o.pickup_time}</div>}
            </div>
          )}
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${allSent ? "bg-[#EAF0E9] text-[#3B503A]" : "bg-[#FDECE4] text-[#C85A32]"}`}>{allSent ? "Tutte inviate" : "In corso"}</span>
      </div>

      <div className="mt-3 space-y-2">
        {orderedCourses.map((cnum) => {
          const items = itemsByCourse[cnum];
          const isSent = sent.has(cnum);
          const meta = COURSE_MAP[cnum] || { n: cnum, label: `Portata ${cnum}`, emoji: "🍽", color: "#6B625B" };
          return (
            <div key={cnum} data-testid={`course-block-${o.id}-${cnum}`} className={`rounded-xl border p-2.5 ${isSent ? "bg-[#EAF0E9]/50 border-[#3B503A]/30" : "bg-[#FBF9F5] border-[#E8E2D5]"}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black" style={{ color: meta.color }}>
                  <span className="text-base">{meta.emoji}</span> {meta.label}
                </div>
                {isSent ? (
                  <div className="flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 text-[10px] font-black text-[#3B503A] uppercase tracking-wider"><CheckCircle2 size={11}/> Inviata</span>
                    <button
                      data-testid={`undo-course-${o.id}-${cnum}`}
                      onClick={() => setConfirmUndo(cnum)}
                      title="Annulla invio in cucina (portata inviata per errore)"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#8C580C]/30 text-[#8C580C] text-[10px] font-bold hover:bg-[#FEF3D6]"
                    >
                      <Undo2 size={9}/> Annulla
                    </button>
                  </div>
                ) : (
                  <button data-testid={`send-course-${o.id}-${cnum}`} onClick={() => onSendCourse(o, cnum)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] hover:brightness-110 text-white text-[10px] font-black uppercase tracking-wider shadow">
                    <Send size={10}/> Manda in cucina
                  </button>
                )}
              </div>
              {confirmUndo === cnum && (
                <div className="mb-2 rounded-lg bg-[#FEF3D6] border border-[#D99B26] p-2 text-[11px]">
                  Sicuro di annullare l'invio in cucina di {meta.label}?
                  <div className="mt-1.5 flex gap-1.5">
                    <button data-testid={`undo-confirm-${o.id}-${cnum}`} onClick={() => { onUndoCourse(o, cnum); setConfirmUndo(null); }} className="px-2.5 py-1 rounded-full bg-[#8C580C] text-white text-[10px] font-bold">Sì, annulla</button>
                    <button onClick={() => setConfirmUndo(null)} className="px-2.5 py-1 rounded-full border border-[#8C580C]/30 text-[#8C580C] text-[10px] font-bold">No</button>
                  </div>
                </div>
              )}
              <div className="space-y-0.5 text-xs">
                {items.map((it, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span><span className="font-bold text-[#C85A32]">{it.qty}×</span> {it.name}</span>
                    <span className="text-[#6B625B]">€ {(it.price * it.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {o.notes && <div className="mt-2 text-[11px] italic text-[#8C580C] bg-[#FEF3D6] rounded-lg p-2">Note: {o.notes}</div>}

      <div className="mt-3 flex gap-2 flex-wrap">
        {o.status === "new" && (
          <button data-testid={`revert-new-${o.id}`} onClick={() => onSetStatus(o, "new")} className="hidden"/>
        )}
        {allSent && o.status !== "served" && (
          <button data-testid={`mark-served-${o.id}`} onClick={() => onMarkServed(o)} className="flex-1 inline-flex items-center justify-center gap-1 bg-[#3B503A] hover:bg-[#2f4030] text-white rounded-full py-2 text-xs font-semibold"><Check size={12}/> Segna servito</button>
        )}
        {o.status === "served" && (
          <button data-testid={`revert-preparing-${o.id}`} onClick={() => onSetStatus(o, "preparing")} className="inline-flex items-center justify-center gap-1 border border-[#E8E2D5] hover:bg-[#F0EBE1] rounded-full py-2 px-3 text-xs font-semibold text-[#8C580C]"><Undo2 size={11}/> Torna a "In preparazione"</button>
        )}
        {o.status === "served" && (
          <button data-testid={`close-order-${o.id}`} onClick={() => onClose(o)} className="flex-1 inline-flex items-center justify-center gap-1 border border-[#E8E2D5] hover:bg-[#F0EBE1] rounded-full py-2 text-xs font-semibold text-[#6B625B]"><X size={12}/> Chiudi ordine</button>
        )}
      </div>
    </div>
  );
}

export default function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [guests, setGuests] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("new");

  const load = async () => {
    try {
      const [o, g] = await Promise.all([api.get("/orders"), api.get("/guests")]);
      setOrders(o.data); setGuests(g.data.length);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const sendCourse = async (order, courseNum) => {
    try {
      const { data } = await api.put(`/orders/${order.id}/send-course`, { course: courseNum });
      setOrders((prev) => prev.map((x) => x.id === data.id ? data : x));
      const meta = COURSE_MAP[courseNum];
      toast.success(`${meta?.emoji || "🍽"} ${meta?.label || "Portata"} inviata in cucina`);
    } catch { toast.error("Errore invio portata"); }
  };
  const undoCourse = async (order, courseNum) => {
    try {
      const { data } = await api.put(`/orders/${order.id}/undo-course`, { course: courseNum });
      setOrders((prev) => prev.map((x) => x.id === data.id ? data : x));
      const meta = COURSE_MAP[courseNum];
      toast.success(`${meta?.label || "Portata"} annullata — inviata per errore`);
    } catch { toast.error("Errore annullamento"); }
  };
  const setStatus = async (order, status) => {
    try {
      await api.put(`/orders/${order.id}`, { status });
      toast.success(`Ordine → ${status}`);
      load();
    } catch { toast.error("Errore"); }
  };
  const markServed = (order) => setStatus(order, "served");
  const closeOrder = (order) => setStatus(order, "closed");

  const newOrders = orders.filter((o) => o.status === "new");
  const preparing = orders.filter((o) => o.status === "preparing");
  const served = orders.filter((o) => o.status === "served");
  const counts = { new: newOrders.length, preparing: preparing.length, served: served.length };

  const renderColumn = (id) => {
    const list = id === "new" ? newOrders : id === "preparing" ? preparing : served;
    if (list.length === 0) return <EmptyCol icon={id === "new" ? ClipboardList : id === "preparing" ? ChefHat : Check} text={id === "new" ? "Nessun nuovo ordine" : id === "preparing" ? "Nessun ordine in preparazione" : "Nessun ordine servito"}/>;
    return <div className="space-y-3">{list.map((o) => <OrderCard key={o.id} o={o} onSendCourse={sendCourse} onUndoCourse={undoCourse} onMarkServed={markServed} onClose={closeOrder} onSetStatus={setStatus}/>)}</div>;
  };

  return (
    <div data-testid="orders-panel" className="bg-white rounded-3xl border border-[#E8E2D5] shadow-sm overflow-hidden">
      <div className="p-5 pb-3 border-b border-[#E8E2D5]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold">Menuparty · Comanda pro</div>
            <h3 className="font-serif-display text-2xl font-bold mt-1">Ordini · Sala, Asporto, Delivery</h3>
            <p className="text-[11px] text-[#6B625B] mt-1">Invia le portate a piacere. Sbaglio in cucina? Annulla con un tap. Torna a "In preparazione" da servito.</p>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <div className="inline-flex items-center gap-1 text-[#3B503A]"><Users size={14}/> {guests} ospiti</div>
            <div className="inline-flex items-center gap-1 text-[#C85A32]"><ClipboardList size={14}/> {orders.filter((o) => o.status !== "closed").length} attivi</div>
          </div>
        </div>
      </div>

      <div className="md:hidden flex border-b border-[#E8E2D5]">
        {TABS.map((t) => {
          const Icon = t.icon; const active = tab === t.id;
          return (
            <button key={t.id} data-testid={`orders-tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex-1 py-3 text-xs font-bold inline-flex flex-col items-center gap-0.5 relative ${active ? "text-[#C85A32]" : "text-[#6B625B]"}`}>
              <Icon size={16}/> <span>{t.label}</span>
              {counts[t.id] > 0 && <span className="absolute top-1.5 right-4 bg-[#C85A32] text-white text-[9px] min-w-[16px] h-[16px] px-1 rounded-full grid place-items-center font-bold">{counts[t.id]}</span>}
              {active && <span className="absolute bottom-0 inset-x-0 h-0.5 bg-[#C85A32]"/>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="p-8 text-sm text-[#6B625B] text-center">Caricamento...</div>
      ) : (
        <>
          <div className="md:hidden p-4 max-h-[70vh] overflow-y-auto bg-[#FBF9F5]">{renderColumn(tab)}</div>
          <div className="hidden md:grid grid-cols-3 gap-0">
            {TABS.map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={t.id} data-testid={`orders-col-${t.id}`} className={`p-4 min-h-[400px] max-h-[70vh] overflow-y-auto bg-[#FBF9F5] ${i < 2 ? "border-r border-[#E8E2D5]" : ""}`}>
                  <div className="flex items-center justify-between mb-3 sticky top-0 bg-[#FBF9F5] pb-2 z-10">
                    <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-[#231F1D]"><Icon size={13} className="text-[#C85A32]"/> {t.label}</div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white border border-[#E8E2D5] text-[#6B625B]">{counts[t.id]}</span>
                  </div>
                  {renderColumn(t.id)}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function EmptyCol({ icon: Icon, text }) {
  return (
    <div className="text-center py-8 text-[#6B625B] text-xs">
      <Icon size={28} className="mx-auto text-[#C85A32]/30"/>
      <div className="mt-2">{text}</div>
    </div>
  );
}
